<template>
    <hts-map
        ref="map"
        :center-position="centerPosition"
        @click="handleClick" />
</template>

<script lang="ts" setup>
interface IRoute {
  originData: google.maps.LatLngLiteral
  destinationData: google.maps.LatLngLiteral
  waypointsData: {
    location: google.maps.LatLngLiteral
  }[]
}

const props = defineProps({
  mapStyle: {
    type: String,
    default: ''
  },
  centerPosition: Object,
  centerMarker: Boolean,
  nodeList: {
    type: Array,
    default: () => []
  },
  route: {
    type: null || (Object as () => IRoute),
    default: null
  },
  last24hPositions: {
    type: Array,
    default: () => []
  }
})
const emits = defineEmits(['click', 'clickMarker'])
const map: any = ref(null)
const { nodeList, centerPosition, centerMarker, route, last24hPositions } = toRefs(props)

const handleClick = (location: google.maps.LatLng | google.maps.LatLngLiteral, place: google.maps.places.PlaceResult) => {
  emits('click', location, place)
}

onMounted(() => {
  renderNodeList()
  renderRoute()
  renderPolyline(last24hPositions.value)
})

watch(
  () => centerMarker.value,
  () => {
    map.value &&
      map.value.ready().then(() => {
        if (centerPosition?.value) {
          map.value.createAdvanceMarker(centerPosition.value as google.maps.LatLng | google.maps.LatLngLiteral)
        }
      })
  },
  { deep: true }
)

watch(
  () => centerPosition?.value,
  (val: Object) => {
    map.value &&
      map.value.ready().then(() => {
        if (centerPosition?.value) {
          map.value.removeMarkers()
          map.value.createMarker(val as google.maps.LatLng | google.maps.LatLngLiteral)
        }
      })
  },
  { deep: true }
)

watch(
  () => nodeList.value,
  () => {
    renderNodeList()
  },
  { deep: true }
)

watch(
  () => nodeList.value,
  () => {
    renderNodeList()
  },
  { deep: true }
)

watch(
  () => route.value,
  (val: any) => {
    if (!val) {
      if (!map.value) {
        return
      }
      map.value.clearDirection()
    }
    renderRoute()
  },
  { deep: true }
)

watch(
  () => last24hPositions,
  (val: any) => {
    if (!val) {
      if (!map.value) {
        return
      }
      map.value.clearDirection()
    }
    renderPolyline(val)
  },
  { deep: true }
)

const renderNodeList = () => {
  if (!map.value) {
    return
  }
  map.value.ready().then(() => {
    map.value.removeAllMarkers()
    nodeList.value.forEach(async (node: any) => {
      const htmlContent = document.createElement('div')
      htmlContent.className = 'hts-marker ' + (node.className || '')
      const text = document.createElement('span')
      const icon = document.createElement('img')
      icon.src = node.icon
      text.textContent = node.title || $t('labels.unknown')
      htmlContent.appendChild(icon)
      htmlContent.appendChild(text)
      const marker = await map.value.createAdvanceMarker(node.position as google.maps.LatLng | google.maps.LatLngLiteral, htmlContent)
      marker.addListener('click', () => {
        emits('clickMarker', node)
        map.value.hideInfoWindow()
        map.value.hideClickPositionMarker()
      })
    })
  })
}

const renderRoute = () => {
  if (!route.value) {
    return
  }

  if (!map.value) {
    return
  }

  map.value.ready().then(() => {
    const { originData, destinationData, waypointsData } = route.value
    map.value && map.value.drawDirection(originData, destinationData, 'DRIVING', waypointsData)
  })
}

const renderPolyline = (val: any) => {
  if (!last24hPositions.value) {
    return
  }

  if (!map.value) {
    return
  }

  map.value.ready().then(() => {
    map.value && map.value.drawPolyline(val)
  })
}
</script>
